import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { invokeFunction, supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import { LenderDashboard } from "@/components/admin/lenders/LenderDashboard";
import type { LenderDashboardData } from "@/components/admin/lenders/lenderDashboardStats";

interface LenderDashboardResponse extends LenderDashboardData {
  email: string;
  lenderProfileId: string | null;
}

const errorStatus = (err: unknown): number | undefined =>
  (err as { context?: { status?: unknown } } | null)?.context?.status as number | undefined;

export default function PublicLenderDashboard() {
  const [searchParams] = useSearchParams();
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessData, setAccessData] = useState<LenderDashboardResponse | null>(null);

  useEffect(() => {
    validateToken();
  }, [searchParams]);

  const validateToken = async () => {
    const token = searchParams.get("token");

    if (!token) {
      setError("No access token provided");
      setIsValidating(false);
      return;
    }

    try {
      const { data } = await invokeFunction<LenderDashboardResponse>(supabase, "lender-dashboard-v1", {
        body: { token },
      });
      if (!data) throw new Error("Empty response");
      setAccessData(data);
    } catch (err) {
      const status = errorStatus(err);
      if (status === 410) {
        setError("Access token has expired");
      } else if (status === 400 || status === 401) {
        setError("Invalid or expired access token");
      } else {
        console.error("Error validating token:", err);
        setError("Failed to validate access token");
      }
    } finally {
      setIsValidating(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            <p>Validating access...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground mt-4">
            Please contact your administrator for assistance.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">
            {accessData?.lenderProfileId ? "Your Lender Dashboard" : "Lender Dashboard"}
          </h1>
          <p className="text-muted-foreground">
            Viewing as: {accessData?.email}
          </p>
        </div>
        <LenderDashboard lenderProfileId={accessData?.lenderProfileId || undefined} data={accessData ?? undefined} />
      </div>
    </div>
  );
}
